import { Fragment, useMemo } from "react";
import { Lock, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  PlanningBlock,
  PlanningCard,
  PlanningCell,
  PlanningPickIntent,
  PlanningPickup,
  PlanningSlotRef,
  PlanningTruck,
} from "../../types/planning";
import { PlanningSlot } from "./PlanningSlot";
import { cellsOf } from "../../utils/planningBoard";

/**
 * The board itself: trucks down, days and slots across.
 *
 * ── Two renderings of one grid, and why not one responsive table ─────────────
 *
 * A truck-week is four slots × seven days = twenty-eight cells per truck, and
 * on a desktop that is a table — which is the right shape, because the value of
 * the board is comparison across rows. You are looking for the truck with a gap
 * on Thursday, and a table is the only layout where that is one glance.
 *
 * On a phone it is not a table and no amount of horizontal scrolling makes it
 * one: a 320px viewport shows a third of one cell, so the comparison the table
 * exists for is gone and what is left is a worse list. So the small screen gets
 * an actual list — truck by truck, day by day, slots stacked — which is a
 * genuinely different presentation of the same data rather than the same table
 * squeezed. The interaction is identical in both, because picking a card up is
 * state rather than a drag (see `PlanningPickup`), and both call the same
 * `PlanningSlot`.
 *
 * The breakpoint is `lg`. Tablets in landscape get the table, which is what a
 * dispatcher at a desk with an iPad actually wants; portrait and phones get the
 * list.
 */

interface Props {
  mode: "week" | "day";
  days: string[];
  /** Per-truck days to draw (the availability filter); all `days` when absent. */
  daysFor?: (truckId: string) => string[];

  freeSlotsOn?: (vehicleId: string, date: string) => number;
  /** Why no truck is drawn; the generic search line when absent. */
  noTrucksMessage?: string;
  slotCount: number;
  trucks: PlanningTruck[];
  assignments: PlanningCard[];
  blocks: PlanningBlock[];
  picked: PlanningPickup | null;
  /** The container whose slots are being edited, if any. */
  editing: PlanningCard | null;
  busyId: string | null;

  onPick: (pickup: PlanningPickup, intent?: PlanningPickIntent) => void;
  onDrop: (slotRef: PlanningSlotRef) => void;
  onSend: (card: PlanningCard) => void;
  onUnsend: (card: PlanningCard) => void;
  onUnassign: (card: PlanningCard) => void;
  onOpen: (card: PlanningCard) => void;
  mapOpenId: string | null;
  onToggleMap: (card: PlanningCard) => void;
  onBlock: (slotRef: PlanningSlotRef, reason?: string) => void;
  onRelease: (block: PlanningBlock) => void;
  onEditSlots: (card: PlanningCard) => void;
  onAddSlot: (slotRef: PlanningSlotRef) => void;
  onRemoveSlot: (card: PlanningCard, cell: PlanningCell) => void;

  /**
   * Is this container inside the handler filter? `undefined` is everything.
   *
   * A *drawing* decision and nothing more. `assignments` arrives whole whatever
   * the filter says — and must, because `freeSlotsOn` is computed from it — so
   * a job outside the scope collapses to a grey tile and its cell stays
   * occupied. See the branch in `PlanningSlot` for why hiding it would be a
   * double-booking waiting to happen.
   */
  inScope?: (card: PlanningCard) => boolean;
  /** Resolves a handler id to a name, for the tiles and the cards. */
  handlerName?: (id: string | null | undefined) => string;
}

/** A cell's occupant, and which of the container's cells this one is. */
interface CellEntry {
  card: PlanningCard;
  /** 1-based position among the container's cells, earliest first. */
  index: number;
  total: number;
}

/** `vehicleId|date|slot` — the key both lookups are built on. */
const cellKey = (vehicleId: string, date: string, slot: number) =>
  `${vehicleId}|${date}|${slot}`;

export function PlanningGrid(props: Props) {
  const {
    mode,
    days,
    slotCount,
    trucks,
    assignments,
    blocks,
    picked,
    editing,
    busyId,
  } = props;

  const { t, i18n } = useTranslation("calendar");

  /**
   * Both grids indexed once, by cell.
   *
   * A truck-week draws 280 cells for ten trucks, and finding each one's occupant
   * by scanning the assignment list would be 280 passes over it on every
   * keystroke in the pool's search box. Two maps built once per render is the
   * difference between a board that responds and one that stutters on a fleet.
   */
  const cards = useMemo(() => {
    const byCell = new Map<string, CellEntry>();
    for (const card of assignments) {
      if (!card.vehicleId) continue;
      // Every cell the container holds, so an extra slot is as taken on the
      // grid as the first one is.
      const cells = cellsOf(card);
      cells.forEach((cell, i) =>
        byCell.set(cellKey(card.vehicleId!, cell.date, cell.slot), {
          card,
          index: i + 1,
          total: cells.length,
        }),
      );
    }
    return byCell;
  }, [assignments]);

  const blocked = useMemo(() => {
    const byCell = new Map<string, PlanningBlock>();
    for (const block of blocks) {
      byCell.set(cellKey(block.vehicleId, block.date, block.slot), block);
    }
    return byCell;
  }, [blocks]);

  /** How many real containers a truck carries across the window. */
  const loadOf = (vehicleId: string) =>
    assignments.filter((card) => card.vehicleId === vehicleId).length;

  /**
   * How many of this truck's slots are held back across the window.
   *
   * Shown on the truck's own header so occupancy is legible without reading
   * twenty-eight cells. It is also what stops the idle highlight from lying: a
   * truck whose whole week is blocked for a service has no jobs, and painting it
   * amber-as-idle would send a dispatcher hunting for capacity that somebody has
   * already ruled out.
   */
  const heldOf = (vehicleId: string) =>
    blocks.filter((block) => block.vehicleId === vehicleId).length;

  const slots = Array.from({ length: slotCount }, (_, index) => index + 1);

  const dayLabel = (day: string, long = false) =>
    new Date(`${day}T00:00:00`).toLocaleDateString(i18n.language, {
      weekday: long ? "long" : "short",
      day: "numeric",
      month: long ? "short" : undefined,
    });

  const freeBadge = (truck: PlanningTruck, day: string) => {
    const free = props.freeSlotsOn?.(truck.id, day);
    if (free === undefined) return null;
    return (
      <span
        className={`text-[9.5px] font-semibold ${
          free === slotCount
            ? "text-orange-700"
            : free === 0
              ? "text-gray-400"
              : "text-gray-500"
        }`}
      >
        {t("planning.grid.freeSlots", { count: free })}
      </span>
    );
  };

  const slotFor = (truck: PlanningTruck, day: string, slot: number) => {
    const key = cellKey(truck.id, day, slot);
    const entry = cards.get(key) ?? null;
    return (
      <PlanningSlot
        slotRef={{ vehicleId: truck.id, date: day, slot }}
        entry={entry}
        block={blocked.get(key) ?? null}
        picked={picked}
        editing={editing}
        busyId={busyId}
        onPick={props.onPick}
        onDrop={props.onDrop}
        onSend={props.onSend}
        onUnsend={props.onUnsend}
        onUnassign={props.onUnassign}
        onOpen={props.onOpen}
        mapOpenId={props.mapOpenId}
        onToggleMap={props.onToggleMap}
        onBlock={props.onBlock}
        onRelease={props.onRelease}
        onEditSlots={props.onEditSlots}
        onAddSlot={props.onAddSlot}
        onRemoveSlot={props.onRemoveSlot}
        // An empty cell is always "in scope" — there is nothing to collapse.
        inScope={!entry || !props.inScope || props.inScope(entry.card)}
        handlerName={props.handlerName}
      />
    );
  };

  if (!trucks.length) {
    return (
      <p className="py-16 text-center text-[13px] text-gray-400">
        {props.noTrucksMessage ?? t("planning.grid.noTrucks")}
      </p>
    );
  }

  return (
    <>
      {/* ── Desktop / landscape tablet: the grid as a grid ───────────────── */}
      <div className="hidden overflow-auto lg:block">
        <table className="w-full table-fixed border-separate border-spacing-0 bg-white">
          <colgroup>
            <col className="w-[82px]" />
            {mode === "week" && <col className="w-[110px]" />}
            {slots.map((slot) => (
              <col key={slot} className="w-[230px]" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="sticky top-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-2.5 py-2.5 text-left text-[11.5px] font-semibold text-gray-500">
                {t("planning.grid.truck")}
              </th>
              {mode === "week" && (
                <th className="sticky top-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-2.5 py-2.5 text-left text-[11.5px] font-semibold text-gray-500">
                  {t("planning.grid.day")}
                </th>
              )}
              {slots.map((slot) => (
                <th
                  key={slot}
                  className="sticky top-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-2.5 py-2.5 text-left text-[11.5px] font-semibold text-gray-500"
                >
                  {t("planning.grid.slot", { slot })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trucks.map((truck) => {
              const load = loadOf(truck.id);
              const held = heldOf(truck.id);
              const truckDays = props.daysFor?.(truck.id) ?? days;
              return (
                <Fragment key={truck.id}>
                  {truckDays.map((day, dayIndex) => (
                    <tr key={day}>
                      {dayIndex === 0 && (
                        <td
                          rowSpan={truckDays.length}
                          title={[truck.plate, truck.model].filter(Boolean).join(" · ") || undefined}
                          className={`border-b border-r border-gray-200 px-1.5 py-2 text-center align-middle ${
                            load === 0 && held === 0 ? "bg-orange-50" : "bg-gray-50"
                          }`}
                        >
                          <div className="font-mono text-[12.5px] font-bold text-dark-900">
                            {truck.name}
                          </div>
                          <div
                            className={`mt-1 text-[9.5px] font-semibold ${load === 0 && held === 0 ? "text-orange-700" : "text-gray-500"}`}
                          >
                            {t("planning.grid.jobs", { count: load })}
                          </div>
                          {mode === "day" && (
                            <div className="mt-1">{freeBadge(truck, day)}</div>
                          )}
                          {mode === "week" && truckDays.length < days.length && (
                            <div className="mt-1 text-[9px] font-semibold text-gray-400">
                              {t("planning.grid.daysShown", {
                                shown: truckDays.length,
                                total: days.length,
                              })}
                            </div>
                          )}
                          {held > 0 && (
                            <div className="mt-1 inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-900">
                              <Lock className="h-2.5 w-2.5" />
                              {t("planning.grid.held", { count: held })}
                            </div>
                          )}
                        </td>
                      )}
                      {mode === "week" && (
                        <td className="border-b border-r border-gray-200 px-2.5 py-2 align-middle text-[11.5px] font-semibold text-gray-500">
                          {dayLabel(day, true)}
                          <div className="mt-0.5">{freeBadge(truck, day)}</div>
                        </td>
                      )}
                      {slots.map((slot) => (
                        <td
                          key={slot}
                          className="border-b border-r border-gray-200 p-1.5 align-top"
                        >
                          {slotFor(truck, day, slot)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* A gap between trucks, so twenty-eight cells read as one
                      truck's week rather than as an undifferentiated grid. */}
                  <tr>
                    <td
                      colSpan={slots.length + (mode === "week" ? 2 : 1)}
                      className="h-2 bg-gray-50"
                    />
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Phone / portrait tablet: the same board as a list ────────────── */}
      <div className="space-y-3 lg:hidden">
        {trucks.map((truck) => {
          const load = loadOf(truck.id);
          const held = heldOf(truck.id);
          return (
            <section
              key={truck.id}
              className="overflow-hidden rounded-xl border border-gray-200 bg-white"
            >
              <header
                className={`flex items-center justify-between gap-2 px-3 py-2.5 ${
                  load === 0 && held === 0 ? "bg-orange-50" : "bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Truck
                    className={`h-4 w-4 ${load === 0 && held === 0 ? "text-orange-600" : "text-gray-500"}`}
                  />
                  <span className="font-mono text-[13px] font-bold text-dark-900">
                    {truck.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {held > 0 && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-900">
                      <Lock className="h-2.5 w-2.5" />
                      {t("planning.grid.held", { count: held })}
                    </span>
                  )}
                  <span
                    className={`text-[11px] font-semibold ${load === 0 && held === 0 ? "text-orange-700" : "text-gray-500"}`}
                  >
                    {t("planning.grid.jobs", { count: load })}
                  </span>
                </div>
              </header>

              <div className="divide-y divide-gray-100">
                {(props.daysFor?.(truck.id) ?? days).map((day) => (
                  <div key={day} className="px-3 py-2.5">
                    <div className="mb-2 flex items-baseline gap-2 text-[11.5px] font-semibold text-gray-500">
                      {dayLabel(day, true)}
                      {freeBadge(truck, day)}
                    </div>
                    {/*
                     * Two columns on a tablet, one on a phone. Four stacked
                     * slots is a long scroll per day; two abreast halves it
                     * without making a cell too narrow to read a city out of.
                     */}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {slots.map((slot) => (
                        <div key={slot}>
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                            {t("planning.grid.slot", { slot })}
                          </div>
                          {slotFor(truck, day, slot)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}
